import { useMemo, useState } from 'react'
import {
  AlertOctagon,
  ArrowUpRight,
  CheckCircle2,
  RotateCcw,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
} from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ApiError } from '@/api/client'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog'
import { useCan } from '@/lib/permissions'
import { formatNaira, formatTimeAgo } from '@/lib/format'
import { RequestAccessButton } from '@/features/access-requests/RequestAccessButton'
import {
  approveCase,
  changeCaseStatus,
  declineCase,
  escalateCase,
  referCaseToHQ,
} from '../api'
import { Building2 } from 'lucide-react'
import { caseDetailQueryKey } from '../hooks/useCaseDetail'
import type { CaseDetail } from '../types'

/**
 * DispositionPanel — the case-detail surface where the analyst
 * finalises the investigation. Renders supporting-evidence
 * snapshots derived from the case + decision buttons that map to
 * the four canonical outcomes:
 *
 *   - Approve (false positive) → /cases/{id}/actions/approve
 *   - Decline (fraud confirmed) → /cases/{id}/actions/decline
 *   - Escalate (to AML/SAR or supervisor) → /cases/{id}/actions/escalate
 *   - Reopen (terminal → in_review) → /cases/{id}/status
 *
 * Each button opens a confirm dialog asking for a reason ≥ 10
 * chars. Outcomes are stated explicitly so the analyst knows what
 * happens to the case + linked transactions before they click.
 */
export function DispositionPanel({ c }: { c: CaseDetail }) {
  const canDispose = useCan('cases:write')
  const supporting = useMemo(() => deriveSupportingEvidence(c), [c])

  const [open, setOpen] = useState<DispositionKind | null>(null)
  // The wire status is `open | in_progress | pending_info |
  // escalated | resolved | closed`. The legacy UX aliases
  // (`fraud_confirmed`, `false_positive`) live in the type union
  // for the list view; in this panel we treat them as terminal so
  // the dialog doesn't double-close a case.
  const status = c.status as string
  const isTerminal =
    status === 'resolved' ||
    status === 'closed' ||
    status === 'fraud_confirmed' ||
    status === 'false_positive'

  return (
    <Card id="disposition" className="border-primary/30 bg-primary/[0.02]">
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2">
            <Sparkles size={14} className="text-primary" aria-hidden />
            Disposition · {prettyStatus(c.status)}
          </CardTitle>
          {isTerminal ? (
            <Badge variant="success" className="gap-1">
              <CheckCircle2 size={10} aria-hidden /> Investigation closed
            </Badge>
          ) : (
            <Badge variant="pending" className="gap-1">
              <ShieldAlert size={10} aria-hidden /> Awaiting decision
            </Badge>
          )}
        </div>
        <p className="text-xs text-muted-fg">
          The expected outcome of this investigation is one of the
          four dispositions below. Each writes an audit row, flips
          the linked transactions, and moves the case to its
          terminal state.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Supporting evidence summary */}
        <SupportingEvidence c={c} bag={supporting} />

        {/* Decision buttons */}
        {canDispose ? (
          <div className="grid gap-2 border-t border-border pt-3 sm:grid-cols-2 lg:grid-cols-4">
            <DecisionTile
              tone="success"
              icon={<ShieldCheck size={14} className="text-status-success" aria-hidden />}
              label="Approve"
              outcome="Resolves the case as false positive and approves the linked transaction. If the case is still open, it'll be taken into in_progress first."
              disabled={isTerminal}
              onClick={() => setOpen('approve')}
            />
            <DecisionTile
              tone="failed"
              icon={<AlertOctagon size={14} className="text-status-failed" aria-hidden />}
              label="Decline · Confirm fraud"
              outcome="Resolves the case as fraud confirmed and declines the linked transaction. Triggers SAR workflow. Open cases are auto-taken first."
              disabled={isTerminal}
              onClick={() => setOpen('decline')}
            />
            <DecisionTile
              tone="pending"
              icon={<ArrowUpRight size={14} className="text-status-pending" aria-hidden />}
              label="Escalate"
              outcome="Moves the case to escalated. The senior analyst on call picks it up; SLA timer resets."
              disabled={isTerminal}
              onClick={() => setOpen('escalate')}
            />
            <DecisionTile
              tone="info"
              icon={<RotateCcw size={14} className="text-primary" aria-hidden />}
              label="Reopen"
              outcome="Moves a resolved case back to open and clears the prior resolution. Use only when new evidence arrived after closure."
              disabled={status !== 'resolved'}
              onClick={() => setOpen('reopen')}
            />
          </div>
        ) : null}
        {canDispose ? (
          <div className="mt-2 flex items-center justify-between gap-2 rounded border border-dashed border-border p-2 text-xs">
            {/* BR-014 — secondary action: refer to HQ. Visually
                distinct from the canonical disposition tiles because
                it doesn't close the case; it just adds HQ visibility. */}
            <span className="flex items-center gap-2 text-muted-fg">
              <Building2 size={12} aria-hidden />
              {c.escalated_to_hq_at ? (
                <span>Referred to HQ for oversight.</span>
              ) : (
                <span>Need HQ oversight without changing branch ownership?</span>
              )}
            </span>
            {!c.escalated_to_hq_at ? (
              <Button size="sm" variant="outline" onClick={() => setOpen('refer_hq')}>
                Refer to HQ
              </Button>
            ) : null}
          </div>
        ) : null}
        {!canDispose ? (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded border border-dashed border-border p-3 text-xs text-muted-fg">
            <span>
              Disposition requires the <code>cases:write</code> permission. You
              can ask a senior analyst to grant it for this case in a bounded
              window.
            </span>
            <RequestAccessButton
              resourceType="case"
              resourceId={c.id}
              scope="cases:write"
              resourceLabel={c.case_number}
            />
          </div>
        ) : null}
      </CardContent>

      {open !== null ? (
        <DispositionDialog
          kind={open}
          caseId={c.id}
          currentStatus={status}
          onClose={() => setOpen(null)}
        />
      ) : null}
    </Card>
  )
}

// ---- supporting evidence -------------------------------------------

function SupportingEvidence({
  c,
  bag,
}: {
  c: CaseDetail
  bag: SupportingBag
}) {
  return (
    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
      <Stat
        label="Linked alerts"
        value={String(c.linked_alerts.length)}
        hint={
          c.linked_alerts.length > 0
            ? `Highest priority: ${bag.highestPriority}`
            : undefined
        }
        tone={c.linked_alerts.length >= 3 ? 'warn' : 'ok'}
      />
      <Stat
        label="Total amount"
        value={formatNaira(bag.totalAmountMinor)}
        hint={
          bag.totalAmountMinor >= 100_000_00
            ? '≥ ₦100,000 — SAR threshold'
            : undefined
        }
        tone={bag.totalAmountMinor >= 100_000_00 ? 'warn' : 'ok'}
      />
      <Stat
        label="Channels in scope"
        value={
          bag.channels.length > 0 ? bag.channels.join(', ') : '—'
        }
        hint={bag.channels.length > 1 ? 'Cross-channel pattern' : undefined}
      />
      <Stat
        label="Case age"
        value={c.created_at ? formatTimeAgo(c.created_at) : '—'}
        hint={
          bag.slaBreached
            ? 'SLA breached'
            : bag.slaDeadline
              ? `Due ${formatTimeAgo(bag.slaDeadline)}`
              : undefined
        }
        tone={bag.slaBreached ? 'fail' : 'ok'}
      />
      {bag.bvnFlag ? (
        <Note
          label="BVN / KYC"
          body="Linked alert flags BVN / KYC mismatch. Verify the customer's documents before approving."
          tone="warn"
        />
      ) : null}
      {bag.profileChange ? (
        <Note
          label="Profile change"
          body="Recent profile / PIN reset detected on the case timeline. Common precursor to ATO."
          tone="warn"
        />
      ) : null}
      {bag.hasRegulatoryBlock ? (
        <Note
          label="Regulatory hold"
          body="Customer is under an active court / NFIU / EFCC block. Approving will conflict with the order."
          tone="fail"
        />
      ) : null}
      {bag.repeatedCounterparty ? (
        <Note
          label="Repeated counterparty"
          body={`Beneficiary ${bag.repeatedCounterparty} appears in ≥2 linked alerts — possible mule pattern.`}
          tone="warn"
        />
      ) : null}
    </div>
  )
}

interface SupportingBag {
  totalAmountMinor: number
  channels: string[]
  highestPriority: string
  bvnFlag: boolean
  profileChange: boolean
  hasRegulatoryBlock: boolean
  repeatedCounterparty: string | null
  slaDeadline: string | null
  slaBreached: boolean
}

function deriveSupportingEvidence(c: CaseDetail): SupportingBag {
  const alerts = c.linked_alerts
  const timeline = c.timeline ?? []
  const cAny = c as unknown as { sla_deadline?: string | null; sla_breached?: boolean }

  let totalAmountMinor = 0
  const channelSet = new Set<string>()
  const counterCounts = new Map<string, number>()
  let bvnFlag = false
  let highestPriority: string = 'low'
  const PRIORITY_ORDER = ['low', 'medium', 'high', 'critical']

  for (const a of alerts) {
    if (typeof a.amount_minor === 'number') totalAmountMinor += a.amount_minor
    if (a.channel) channelSet.add(a.channel)
    const txn = (a as unknown as { transaction?: { beneficiary_nuban?: string } }).transaction
    if (txn?.beneficiary_nuban) {
      counterCounts.set(
        txn.beneficiary_nuban,
        (counterCounts.get(txn.beneficiary_nuban) ?? 0) + 1,
      )
    }
    const flags = (a as unknown as { flags?: string[] }).flags ?? []
    if (flags.some((f) => /bvn|kyc/i.test(f))) bvnFlag = true
    if (
      PRIORITY_ORDER.indexOf(a.priority) >
      PRIORITY_ORDER.indexOf(highestPriority)
    ) {
      highestPriority = a.priority
    }
  }
  const profileChange =
    timeline.some((t) => /profile|reset|pin|kyc/i.test(t.message)) ||
    alerts.some((a) => {
      const f = (a as unknown as { flags?: string[] }).flags ?? []
      return f.some((x) => /profile_change|pin_reset/i.test(x))
    })
  const hasRegulatoryBlock = timeline.some((t) =>
    /regulatory|court|nfiu|efcc/i.test(t.message),
  )
  let repeatedCounterparty: string | null = null
  for (const [n, count] of counterCounts.entries()) {
    if (count >= 2) {
      repeatedCounterparty = n.length > 6 ? `${n.slice(0, 3)}***${n.slice(-3)}` : n
      break
    }
  }
  return {
    totalAmountMinor,
    channels: [...channelSet],
    highestPriority,
    bvnFlag,
    profileChange,
    hasRegulatoryBlock,
    repeatedCounterparty,
    slaDeadline: cAny.sla_deadline ?? null,
    slaBreached: Boolean(cAny.sla_breached),
  }
}

function Stat({
  label,
  value,
  hint,
  tone,
}: {
  label: string
  value: string
  hint?: string
  tone?: 'ok' | 'warn' | 'fail'
}) {
  const toneClass =
    tone === 'fail'
      ? 'border-status-failed/40 bg-status-failed/5'
      : tone === 'warn'
        ? 'border-status-pending/40 bg-status-pending/5'
        : 'border-border bg-card'
  return (
    <div className={`rounded border p-2.5 ${toneClass}`}>
      <p className="text-[0.625rem] uppercase tracking-wider text-muted-fg">
        {label}
      </p>
      <p className="mt-0.5 truncate text-sm font-semibold tabular-nums" title={value}>
        {value}
      </p>
      {hint ? (
        <p className="mt-0.5 text-[0.6875rem] text-muted-fg">{hint}</p>
      ) : null}
    </div>
  )
}

function Note({
  label,
  body,
  tone,
}: {
  label: string
  body: string
  tone: 'warn' | 'fail'
}) {
  const toneClass =
    tone === 'fail'
      ? 'border-status-failed/40 bg-status-failed/5 text-status-failed'
      : 'border-status-pending/40 bg-status-pending/5 text-status-pending'
  return (
    <div className={`col-span-full rounded border p-2.5 ${toneClass}`}>
      <p className="text-[0.625rem] font-semibold uppercase tracking-wider">
        {label}
      </p>
      <p className="mt-0.5 text-xs leading-snug text-fg">{body}</p>
    </div>
  )
}

// ---- decision tile + dialog ----------------------------------------

function DecisionTile({
  icon,
  label,
  outcome,
  tone,
  disabled,
  onClick,
}: {
  icon: React.ReactNode
  label: string
  outcome: string
  tone: 'success' | 'failed' | 'pending' | 'info'
  disabled: boolean
  onClick: () => void
}) {
  const borderClass =
    tone === 'success'
      ? 'border-status-success/30 hover:border-status-success'
      : tone === 'failed'
        ? 'border-status-failed/30 hover:border-status-failed'
        : tone === 'pending'
          ? 'border-status-pending/30 hover:border-status-pending'
          : 'border-primary/30 hover:border-primary'
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`flex flex-col gap-1.5 rounded-md border bg-card p-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${borderClass}`}
    >
      <div className="flex items-center gap-2">
        {icon}
        <span className="text-sm font-semibold">{label}</span>
      </div>
      <p className="text-[0.6875rem] leading-snug text-muted-fg">{outcome}</p>
    </button>
  )
}

type DispositionKind = 'approve' | 'decline' | 'escalate' | 'reopen' | 'refer_hq'

const DISPOSITION_META: Record<
  DispositionKind,
  { title: string; verb: string; outcome: string; minReason: number }
> = {
  approve: {
    title: 'Approve case · false positive',
    verb: 'approve',
    outcome:
      'Closes the case as false_positive and approves the linked transactions. Reversible only by reopening.',
    minReason: 10,
  },
  decline: {
    title: 'Decline case · confirm fraud',
    verb: 'decline',
    outcome:
      'Closes the case as fraud_confirmed, declines the linked transactions, and queues the SAR/STR workflow.',
    minReason: 10,
  },
  escalate: {
    title: 'Escalate case',
    verb: 'escalate',
    outcome:
      'Moves the case to escalated. The senior analyst on call picks it up; SLA timer resets.',
    minReason: 10,
  },
  reopen: {
    title: 'Reopen case',
    verb: 'reopen',
    outcome:
      'Returns a closed case to in_review. Use only when new evidence justifies revisiting the disposition.',
    minReason: 10,
  },
  refer_hq: {
    title: 'Refer case to HQ',
    verb: 'refer to HQ',
    outcome:
      'Flags the case for HQ oversight. Branch ownership + assignee are unchanged; HQ analysts pick it up via their workspace filter. Reversible — branch keeps full control.',
    minReason: 10,
  },
}

function DispositionDialog({
  kind,
  caseId,
  currentStatus,
  onClose,
}: {
  kind: DispositionKind
  caseId: string
  currentStatus: string
  onClose: () => void
}) {
  const qc = useQueryClient()
  const meta = DISPOSITION_META[kind]
  const [reason, setReason] = useState('')

  // The workflow only allows `→ resolved` from `in_progress`,
  // `pending_info`, or `escalated`. If the analyst hasn't taken
  // the case yet (status === 'open'), promote it to in_progress
  // first so approve/decline don't 409. Escalate is allowed from
  // open natively, and reopen lives on the status endpoint.
  const needsTake =
    (kind === 'approve' || kind === 'decline') && currentStatus === 'open'

  const mutation = useMutation({
    mutationFn: async () => {
      const reasonText = reason.trim()
      const payload = { reason: reasonText }
      if (needsTake) {
        await changeCaseStatus(caseId, {
          status: 'in_progress',
          reason: reasonText,
        })
      }
      if (kind === 'approve') {
        await approveCase(caseId, payload)
      } else if (kind === 'decline') {
        await declineCase(caseId, payload)
      } else if (kind === 'escalate') {
        await escalateCase(caseId, payload)
      } else if (kind === 'refer_hq') {
        await referCaseToHQ(caseId, reasonText)
      } else {
        // Reopen — wire value is `open` (not the legacy
        // `in_review` alias). Workflow clears resolution + the
        // resolved_at timestamp on this edge.
        await changeCaseStatus(caseId, {
          status: 'open',
          reason: reasonText,
        })
      }
    },
    onSuccess: () => {
      toast.success(`Case ${meta.verb}d`)
      void qc.invalidateQueries({ queryKey: caseDetailQueryKey(caseId) })
      void qc.invalidateQueries({ queryKey: ['cases', 'list-infinite'] })
      onClose()
    },
    onError: (err) => {
      const msg = describeDispositionError(err, kind)
      toast.error(msg)
    },
  })

  const valid = reason.trim().length >= meta.minReason

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{meta.title}</DialogTitle>
          <DialogDescription>{meta.outcome}</DialogDescription>
        </DialogHeader>
        {needsTake ? (
          <div className="rounded border border-status-pending/30 bg-status-pending/5 px-3 py-2 text-xs text-fg">
            Case is currently <code>open</code>. It will be taken
            (moved to <code>in_progress</code>) before this disposition
            is applied — same paper trail, single click.
          </div>
        ) : null}
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault()
            if (valid) mutation.mutate()
          }}
        >
          <label className="flex flex-col gap-1 text-xs">
            <span className="font-medium text-muted-fg">
              Reason (≥{meta.minReason} chars)
            </span>
            <textarea
              // eslint-disable-next-line jsx-a11y/no-autofocus -- modal/dialog focus management; primary control on open
              autoFocus
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              className="w-full rounded border border-input bg-card px-3 py-2 text-sm"
              placeholder={
                kind === 'approve'
                  ? 'e.g. Customer confirmed the transaction in person; receipts match.'
                  : kind === 'decline'
                    ? 'e.g. Customer denies the transactions; mule cluster confirmed via consortium.'
                    : kind === 'escalate'
                      ? 'e.g. Cross-bank ring; senior + AML review needed.'
                      : 'e.g. New CCTV evidence shows the customer was not present.'
              }
            />
            <span className="text-[0.625rem] text-muted-fg">
              {reason.trim().length} / {meta.minReason} characters
            </span>
          </label>
          {mutation.error ? (
            <p className="text-sm text-status-failed">
              {mutation.error instanceof Error
                ? mutation.error.message
                : 'Disposition failed'}
            </p>
          ) : null}
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="submit"
              variant={kind === 'decline' ? 'destructive' : 'primary'}
              disabled={!valid || mutation.isPending}
            >
              {mutation.isPending
                ? 'Submitting…'
                : `Confirm · ${meta.verb}`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function prettyStatus(s: string): string {
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

/**
 * Translate a workflow / API error into something the analyst can
 * act on. ApiError carries the Go-side code (`invalid_transition`,
 * `optimistic_lock`, `invalid_input`, …) and message; we map the
 * common ones to plainer copy + fall back to the raw message for
 * everything else so we never lose information.
 */
function describeDispositionError(err: unknown, kind: DispositionKind): string {
  const verb = DISPOSITION_META[kind].verb
  if (err instanceof ApiError) {
    if (err.code === 'invalid_transition') {
      return `Cannot ${verb} from this status: ${err.message}`
    }
    if (err.code === 'optimistic_lock') {
      return 'Case was updated by someone else — refresh and try again.'
    }
    if (err.code === 'invalid_input') {
      return err.message
    }
    if (err.status === 403) {
      return `You don't have permission to ${verb} this case.`
    }
    if (err.status === 401) {
      return 'Session expired. Please sign in again.'
    }
    return err.message || `Failed to ${verb} the case.`
  }
  if (err instanceof Error) return err.message
  return `Failed to ${verb} the case.`
}
